import React from 'react';
import {
    Box,
    Button,
    Container,
    Divider,
    Grid,
    Link,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

const PRIVACY_URL = 'https://brpadvice.ru/politika-konfidencialnosti/';
const OFFER_URL = 'https://brpadvice.ru/oferta-na-finradar/';
const SUPPORT_EMAIL = 'info@brpadvice.ru';

const footerColumns = [
    {
        title: 'Продукт',
        links: [
            { label: 'Возможности', href: '/#structure' },
            { label: 'Тарифы', href: '/#pricing' },
            { label: 'FAQ', href: '/#faq' },
            { label: 'Вход', href: '/vhod/' },
        ],
        cta: { label: 'Зарегистрироваться бесплатно' },
    },
    {
        title: 'Решения',
        links: [
            { label: 'ФинРадар для арбитражных управляющих', href: '/bankrotstvo/' },
            { label: 'Анализ банковских выписок', href: '/#structure' },
            { label: 'Проверка контрагентов', href: '/#tasks' },
            { label: 'Поиск аномалий в операциях', href: '/#tasks' },
        ],
    },
    {
        title: 'Блог',
        links: [
            { label: 'Все статьи', href: '/blog/' },
            {
                label: 'Что можно понять по банковской выписке за 10 минут',
                href: '/blog/chto-mozhno-ponyat-po-bankovskoy-vypiske-za-10-minut/',
            },
            {
                label: 'Как начать первую проверку в ФинРадар',
                href: '/blog/kak-nachat-pervuyu-proverku-v-finradar/',
            },
        ],
    },
    {
        title: 'Документы',
        links: [
            { label: 'Политика конфиденциальности', href: PRIVACY_URL, external: true },
            { label: 'Пользовательское соглашение', href: OFFER_URL, external: true },
            { label: 'Оферта', href: OFFER_URL, external: true },
            { label: 'Реквизиты', href: OFFER_URL, external: true },
            { label: 'Поддержка', href: `mailto:${SUPPORT_EMAIL}` },
        ],
    },
];

function FooterLink({ link }) {
    return (
        <Link
            href={link.href}
            target={link.external ? '_blank' : undefined}
            rel={link.external ? 'noopener noreferrer' : undefined}
            underline="none"
            sx={{
                display: 'inline-flex',
                alignItems: 'flex-start',
                color: '#475467',
                fontSize: 15,
                lineHeight: 1.55,
                fontWeight: 600,
                '&:hover': {
                    color: '#155EEF',
                },
            }}
        >
            {link.label}
        </Link>
    );
}

export function FooterCta({ onRegisterClick }) {
    return (
        <Box component="section" sx={{ py: { xs: 7, md: 9 }, backgroundColor: '#F5F7FA' }}>
            <Container maxWidth="lg">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 3, md: 5 },
                        borderRadius: 2,
                        border: '1px solid #D0D5DD',
                        backgroundColor: '#FFFFFF',
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={{ xs: 3, md: 4 }}
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        justifyContent="space-between"
                    >
                        <Box sx={{ maxWidth: 720 }}>
                            <Typography
                                component="h2"
                                sx={{
                                    color: '#101828',
                                    fontSize: { xs: 28, md: 38 },
                                    lineHeight: 1.15,
                                    fontWeight: 800,
                                }}
                            >
                                Проверьте выписки на своих данных
                            </Typography>
                            <Typography sx={{ mt: 1.5, color: '#475467', fontSize: { xs: 16, md: 18 }, lineHeight: 1.75 }}>
                                Зарегистрируйтесь, создайте проект и загрузите банковские выписки. ФинРадар поможет структурировать операции, контрагентов и аномалии для дальнейшего анализа.
                            </Typography>
                        </Box>
                        <Stack spacing={1.25} sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
                            <Button
                                onClick={onRegisterClick}
                                variant="contained"
                                endIcon={<ArrowForwardRoundedIcon />}
                                sx={{
                                    minHeight: 52,
                                    borderRadius: 999,
                                    px: 3,
                                    textTransform: 'none',
                                    fontWeight: 800,
                                    backgroundColor: '#155EEF',
                                    boxShadow: 'none',
                                    '&:hover': { backgroundColor: '#004EEB', boxShadow: 'none' },
                                }}
                            >
                                Зарегистрироваться бесплатно
                            </Button>
                            <Typography sx={{ color: '#667085', fontSize: 14, textAlign: { xs: 'left', sm: 'center' } }}>
                                7 дней бесплатно · 1 проект · без банковской карты
                            </Typography>
                        </Stack>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
}

export default function SiteFooter({ onRegisterClick }) {
    return (
        <Box component="footer" sx={{ backgroundColor: '#FFFFFF', borderTop: '1px solid #EAECF0' }}>
            <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 } }}>
                <Grid container spacing={{ xs: 4, md: 5 }}>
                    <Grid item xs={12} lg={4}>
                        <Stack spacing={2.5} sx={{ maxWidth: 430 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box
                                    sx={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #0F172A 0%, #1D4ED8 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#FFFFFF',
                                        fontWeight: 900,
                                    }}
                                >
                                    FR
                                </Box>
                                <Box>
                                    <Typography sx={{ color: '#101828', fontSize: 20, lineHeight: 1.2, fontWeight: 900 }}>
                                        ФинРадар
                                    </Typography>
                                    <Typography sx={{ color: '#667085', fontSize: 13, fontWeight: 700 }}>
                                        Реальная картина бизнеса
                                    </Typography>
                                </Box>
                            </Stack>
                            <Typography sx={{ color: '#344054', lineHeight: 1.7 }}>
                                Сервис для анализа банковских выписок, книг покупок и продаж, контрагентов и операций.
                            </Typography>
                            <Typography sx={{ color: '#98A2B3', fontSize: 14 }}>
                                Поддержка: <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: '#475467', fontWeight: 700 }}>{SUPPORT_EMAIL}</Link>
                            </Typography>
                        </Stack>
                    </Grid>

                    {footerColumns.map((column) => (
                        <Grid key={column.title} item xs={12} sm={6} md={3} lg={2}>
                            <Typography sx={{ mb: 2, color: '#101828', fontWeight: 900 }}>
                                {column.title}
                            </Typography>
                            <Stack spacing={1.25} alignItems="flex-start">
                                {column.links.map((link) => (
                                    <FooterLink key={`${column.title}-${link.label}`} link={link} />
                                ))}
                                {column.cta ? (
                                    <Button
                                        onClick={onRegisterClick}
                                        variant="text"
                                        endIcon={<ArrowForwardRoundedIcon />}
                                        sx={{
                                            mt: 0.5,
                                            p: 0,
                                            minWidth: 0,
                                            color: '#155EEF',
                                            textTransform: 'none',
                                            fontWeight: 900,
                                            justifyContent: 'flex-start',
                                            '&:hover': { backgroundColor: 'transparent', color: '#004EEB' },
                                        }}
                                    >
                                        {column.cta.label}
                                    </Button>
                                ) : null}
                            </Stack>
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: { xs: 4, md: 5 } }} />

                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                    <Typography sx={{ color: '#667085', fontSize: 14 }}>
                        © 2024-2026 ФинРадар. Продукт команды BRP Advice. Все права защищены.
                    </Typography>
                    <Typography sx={{ color: '#98A2B3', fontSize: 14, maxWidth: 640 }}>
                        Пользуясь сервисом, вы соглашаетесь с условиями пользовательского соглашения и оферты.
                    </Typography>
                </Stack>
            </Container>
        </Box>
    );
}
